import React, { Fragment, useEffect } from 'react';
import {
    Hint, Menu, MenuItem, Typeahead
} from 'react-bootstrap-typeahead'; // ES2015
import PropTypes from 'prop-types';
import cn from 'classnames';

export const emptyCityOption = {
    stateId: '',
    state: '',
    localization: '',
    cityState: '',
    stateCity: '',
    value: ''
};

const TypeAheadField = (props) => {
    const {
        selectedOption,
        handleSelect,
        label,
        catalog,
        id,
        // name,
        errorMessage,
        onBlur
    } = props;

    const onChange = ([item]) => {
        if (item) {
            handleSelect(item);
        } else {
            handleSelect(emptyCityOption);
        }
    };

    const typeaheadRef = React.createRef();

    const renderInput = ({
        inputRef,
        referenceElementRef,
        ...inputProps
    }) => (
        <Hint>
            <label
                className={cn(
                    'fds-field',
                    'has-floated-label',
                    'has-no-placeholder',
                    'fds-form-control-full-width',
                    'form-group',
                    { invalid: errorMessage }
                )}
                htmlFor={inputProps.id}
            >
                <input
                    {...inputProps}
                    placeholder=""
                    className={cn('fds-field-input')}
                    ref={(node) => {
                        inputRef(node);
                        referenceElementRef(node);
                    }}
                />
                <span className={cn('fds-field-label')}>
                    {inputProps.placeholder}
                </span>
                {
                    errorMessage && (
                        <span className={cn('fds-validation-message')} role="alert">
                            <span className={cn('fds-icon fds-icon-name-error')}>
                                <svg role="img" className="fds-icon-svg" aria-hidden="true">
                                    <use xlinkHref="#error" />
                                </svg>
                            </span>
                            { errorMessage }
                        </span>
                    )
                }
            </label>
        </Hint>
    );

    const { value } = selectedOption || { value: false };

    useEffect(() => {
        if (!value && typeaheadRef.current) {
            typeaheadRef.current.clear();
        }
    }, [catalog, value]);

    const renderMenu = (results,
        {
            newSelectionPrefix,
            paginationText,
            renderMenuItemChildren,
            ...menuProps
        }) => {
        let index = 0;
        const provinces = Object.groupBy(results, ({ state }) => state);
        const provincesCount = Object.keys(provinces).length;
        const renderItems = items => (items.map((i) => {
            const item = (
                <MenuItem key={index} option={i} position={index}>
                    {i.localization}
                </MenuItem>
            );
            index += 1;
            return item;
        }));

        // 'undefined' will always be a part of the provinces, given that the
        // user is allowed to add their own input to the results list
        const cityOptions = provincesCount === 1
            ? renderItems(Object.values(provinces)[0])
            : Object.keys(provinces)
                .sort()
                .map((province) => {
                    if (!province || province === 'undefined') {
                        return null;
                    }

                    return (
                        <Fragment key={province}>
                            {index !== 0 && <Menu.Divider />}
                            <Menu.Header>{province}</Menu.Header>
                            {
                                renderItems(provinces[province])
                            }
                        </Fragment>
                    );
                });
        return (
            <div className={cn('menu-container')}>
                <Menu {...menuProps}>
                    {cityOptions}
                </Menu>
            </div>
        );
    };

    return (
        <Typeahead
            selected={value ? [selectedOption] : null}
            ref={typeaheadRef}
            labelKey="localization"
            filterBy={['localization', 'value', 'state', 'cityState', 'stateCity']}
            id={id}
            options={catalog}
            placeholder={label}
            onChange={onChange}
            renderInput={renderInput}
            allowNew
            renderMenu={renderMenu}
            onBlur={onBlur}
        />
    );
};

TypeAheadField.defaultProps = {
    errorMessage: '',
    onBlur: () => {}

};

TypeAheadField.propTypes = {
    selectedOption: PropTypes.shape({
        stateId: PropTypes.string,
        state: PropTypes.string,
        localization: PropTypes.string,
        value: PropTypes.string,
        cityState: PropTypes.string,
        stateCity: PropTypes.string
    }).isRequired,
    handleSelect: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    catalog: PropTypes.arrayOf({
        stateId: PropTypes.string,
        state: PropTypes.string,
        value: PropTypes.string,
        localization: PropTypes.string,
        cityState: PropTypes.string,
        stateCity: PropTypes.string
    }).isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    errorMessage: PropTypes.string,
    onBlur: PropTypes.func
};

export default TypeAheadField;
